import gql from 'graphql-tag';
import { useCurrentAccountId } from '@zapier/identity';
import React, { createContext, useCallback, useContext, useEffect, useState, } from 'react';
import { isEmptyObject } from '../utils/isEmptyObject';
import { useLocalStorage } from '@zapier/common-hooks';
import { useMediaProps } from '@zapier/responsive-utils';
import { useTopNav } from './useTopNav';
import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH, UNIVERSAL_SIDEBAR_WIDTH, } from '../constants';
import { readCookie } from '@zapier/cookies';
import { useQuerySession } from './useQuerySession';
const defaultContext = {
    apiHostname: '',
    canForceSidebarOpen: false,
    canTopbarFitBackToAppButton: false,
    canTopbarFitLinks: false,
    currentAccountId: undefined,
    currentUrl: '',
    isLoadingLoggedIn: true,
    isLoadingTopNav: true,
    isLoggedIn: false,
    isOnAppPage: false,
    isSearchFullWidth: true,
    isSidebarControlledByTopbar: false,
    isSidebarForcedOpen: false,
    isSidebarOpenedByTopbar: false,
    isSidebarRendered: false,
    onToggleSidebarVisibility: () => { },
    setIsSidebarForcedOpen: () => { },
    setIsSidebarOpenedByTopbar: () => { },
    setIsSidebarRendered: () => { },
    shouldRenderCallToAction: false,
    shouldRenderSearch: true,
    shouldRenderSidebar: false,
    shouldRenderAccessibleSidebar: false,
    shouldRenderTopbarLinks: false,
    shouldRenderTOS: false,
    shouldRenderUniversalNav: true,
    sidebarWidth: 0,
    topbarSize: 'medium',
    topNavData: {},
    getFullyQualifiedURL: (path) => path || undefined,
};
export const UniversalNavContext = createContext(defaultContext);
export const useUniversalNavContext = () => useContext(UniversalNavContext);
export const isAuthenticatedQuery = gql `
  query isAuthenticatedQuery {
    isAuthenticatedV2
  }
`;
const isCurrentUrlAnAuthPage = (currentUrl) => {
    return (currentUrl.includes('/login') ||
        currentUrl.includes('/sign-up') ||
        currentUrl.includes('/oauth/authorize'));
};
const isCurrentUrlAnAppPage = (currentUrl, mode) => {
    if (mode) {
        return mode === 'in-app' ? true : false;
    }
    return currentUrl.includes('/app/');
};
const isCurrentUrlAnOnboardingPage = (currentUrl) => {
    return currentUrl.includes('/app/onboarding');
};
const shouldRenderSidebarForRoute = ({ currentUrl, mode, }) => {
    return (isCurrentUrlAnAppPage(currentUrl, mode) &&
        !isCurrentUrlAnAuthPage(currentUrl) &&
        !isCurrentUrlAnOnboardingPage(currentUrl));
};
export const shouldRenderTopbarLinksForRoute = ({ currentUrl, mode, }) => {
    return (!isCurrentUrlAnAppPage(currentUrl, mode) &&
        !isCurrentUrlAnAuthPage(currentUrl));
};
const getShouldRenderSidebar = ({ isLoggedIn, canTopbarFitLinks, currentUrl, isSidebarControlledByTopbar, isSidebarOpenedByTopbar, mode, }) => {
    if (isSidebarControlledByTopbar) {
        return isSidebarOpenedByTopbar;
    }
    if (mode && mode === 'navigation') {
        return canTopbarFitLinks === false ? true : false;
    }
    if (mode && mode === 'in-app' && isLoggedIn) {
        return true;
    }
    if (isLoggedIn && shouldRenderSidebarForRoute({ currentUrl, mode })) {
        return true;
    }
    if (!canTopbarFitLinks &&
        shouldRenderTopbarLinksForRoute({ currentUrl, mode })) {
        return true;
    }
    return false;
};
const getShouldRenderAccessibleSidebar = ({ isLoggedIn, canTopbarFitLinks, currentUrl, isSidebarControlledByTopbar, isSidebarOpenedByTopbar, mode, }) => {
    if (isSidebarControlledByTopbar) {
        return !isSidebarOpenedByTopbar;
    }
    if (mode && mode === 'navigation') {
        return canTopbarFitLinks === false ? true : false;
    }
    if (mode && mode === 'in-app' && isLoggedIn && canTopbarFitLinks) {
        return false;
    }
    if (isLoggedIn &&
        shouldRenderSidebarForRoute({ currentUrl, mode }) &&
        !canTopbarFitLinks) {
        return true;
    }
    if (!canTopbarFitLinks &&
        shouldRenderTopbarLinksForRoute({ currentUrl, mode })) {
        return true;
    }
    return false;
};
const getShouldRenderSearch = ({ currentUrl }) => {
    if (isCurrentUrlAnAuthPage(currentUrl) ||
        isCurrentUrlAnOnboardingPage(currentUrl)) {
        return false;
    }
    return true;
};
const getShouldRenderTopbarToggle = ({ isSidebarControlledByTopbar, isLoggedIn, currentUrl, mode, }) => {
    if (!isSidebarControlledByTopbar) {
        return false;
    }
    if (isLoggedIn) {
        return true;
    }
    return shouldRenderTopbarLinksForRoute({
        currentUrl,
        mode,
    });
};
const getSidebarWidth = ({ canForceSidebarOpen, isSidebarForcedOpen, isSidebarRendered, isSidebarControlledByTopbar, }) => {
    if (!isSidebarRendered || isSidebarControlledByTopbar) {
        return 0;
    }
    if (canForceSidebarOpen && isSidebarForcedOpen) {
        return UNIVERSAL_SIDEBAR_WIDTH;
    }
    return UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH;
};
export const UniversalNavProvider = (props) => {
    const zapid = readCookie('zapidentity');
    const { isLoading: isLoadingTopNav, topNav } = useTopNav(zapid || '');
    const { accountData: accountUsageData, accountsListData, currentUrl, mode, sessionData: sessionDataFromProps, shouldRenderTOS, urlPathPrefix, } = props;
    const isOnAppPage = isCurrentUrlAnAppPage(currentUrl, mode);
    const { isSearchFullWidth, isSidebarControlledByTopbar, canForceSidebarOpen, canTopbarFitBackToAppButton, canTopbarFitLinks, topbarSize, } = useMediaProps({
        '(min-width: 0px)': {
            isSidebarControlledByTopbar: true,
            isSearchFullWidth: true,
            canForceSidebarOpen: false,
            canTopbarFitBackToAppButton: false,
            canTopbarFitLinks: false,
            topbarSize: 'small',
        },
        '(min-width: 450px)': {
            canTopbarFitBackToAppButton: isEmptyObject(topNav.callToAction),
            topbarSize: isEmptyObject(topNav.callToAction) ? 'medium' : 'small',
        },
        '(min-width: 650px)': {
            canTopbarFitBackToAppButton: true,
            topbarSize: 'medium',
        },
        '(min-width: 1040px)': {
            isSidebarControlledByTopbar: false,
            isSearchFullWidth: false,
            canTopbarFitLinks: true,
        },
        '(min-width: 1280px)': {
            canForceSidebarOpen: shouldRenderSidebarForRoute({ currentUrl, mode }),
        },
    });
    const shouldRenderTopbarLinks = canTopbarFitLinks &&
        shouldRenderTopbarLinksForRoute({
            currentUrl,
            mode,
        });
    const { data: sessionData } = useQuerySession();
    const currentAccountId = useCurrentAccountId() || (sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentAccountId);
    const isLoggedIn = sessionData ? sessionData.isLoggedIn : false;
    const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
    const [isSidebarForcedOpen, setIsSidebarForcedOpen] = useLocalStorage('UniversalNav:isSidebarForcedOpen');
    useEffect(() => {
        if (isSidebarForcedOpen === null || isSidebarForcedOpen === undefined) {
            setIsSidebarForcedOpen(true);
        }
    }, [isSidebarForcedOpen, setIsSidebarForcedOpen]);
    const [isSidebarRendered, setIsSidebarRendered] = useState(false);
    const shouldRenderSidebar = getShouldRenderSidebar({
        isLoggedIn,
        canTopbarFitLinks,
        currentUrl,
        isSidebarControlledByTopbar,
        isSidebarOpenedByTopbar,
        mode,
    });
    const shouldRenderAccessibleSidebar = getShouldRenderAccessibleSidebar({
        isLoggedIn,
        canTopbarFitLinks,
        currentUrl,
        isSidebarControlledByTopbar,
        isSidebarOpenedByTopbar,
        mode,
    });
    const shouldRenderSearch = getShouldRenderSearch({
        currentUrl,
    });
    const shouldRenderTopbarToggle = getShouldRenderTopbarToggle({
        isSidebarControlledByTopbar,
        isLoggedIn,
        currentUrl,
    });
    const onToggleSidebarVisibility = shouldRenderTopbarToggle
        ? () => {
            setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
        }
        : undefined;
    const sidebarWidth = getSidebarWidth({
        canForceSidebarOpen,
        isSidebarForcedOpen,
        isSidebarRendered,
        isSidebarControlledByTopbar,
    });
    const memoizedFullyQualifiedUrl = useCallback((link) => {
        if (link && urlPathPrefix && link.startsWith('/'))
            return urlPathPrefix + link;
        return link || undefined;
    }, [urlPathPrefix]);
    const value = {
        accountUsageData,
        accountsListData,
        currentAccountId,
        isLoadingTopNav,
        canTopbarFitBackToAppButton,
        canTopbarFitLinks,
        canForceSidebarOpen,
        currentUrl,
        isLoadingLoggedIn: !sessionData,
        isLoggedIn,
        isOnAppPage,
        isSearchFullWidth,
        isSidebarControlledByTopbar,
        isSidebarOpenedByTopbar,
        isSidebarForcedOpen: canForceSidebarOpen && isSidebarRendered ? isSidebarForcedOpen : false,
        isSidebarRendered,
        mode,
        onToggleSidebarVisibility,
        sessionData: sessionDataFromProps,
        setIsSidebarOpenedByTopbar,
        setIsSidebarForcedOpen,
        setIsSidebarRendered,
        shouldRenderCallToAction: !isCurrentUrlAnOnboardingPage(currentUrl),
        shouldRenderSearch,
        shouldRenderSidebar,
        shouldRenderAccessibleSidebar,
        shouldRenderTopbarLinks,
        shouldRenderTOS,
        shouldRenderUniversalNav: true,
        sidebarWidth,
        topbarSize,
        topNavData: topNav,
        urlPathPrefix,
        getFullyQualifiedURL: memoizedFullyQualifiedUrl,
        apiHostname: props.apiHostname || '',
    };
    return (React.createElement(UniversalNavContext.Provider, { value: value }, props.children));
};
//# sourceMappingURL=useUniversalNavContext.js.map
// @zapier/universal-layout@8.3.1 (41acce620)
